/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminLoginPage.tsx (v2)
 * Real Kejja logo · password eye toggle · remember me
 * ============================================================
 */

import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, KeyRound, AlertCircle, Mail, Eye, EyeOff, Shield } from 'lucide-react';
import { useAdminLogin, useAdminVerifyOtp } from '@/hooks/useAdmin';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const loginMut = useAdminLogin();
  const verifyMut = useAdminVerifyOtp();
  const otpRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (step === 'otp') otpRef.current?.focus(); }, [step]);

  const submitCredentials = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    loginMut.mutate({ email, password }, {
      onSuccess: (r) => { setOtpSessionId(r.otpSessionId); setMaskedEmail(r.maskedEmail); setStep('otp'); setOtp(''); },
      onError:   (e) => setErr(e.message || 'Login failed'),
    });
  };

  const submitOtp = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!otpSessionId || otp.length !== 6) { setErr('Enter the 6-digit code.'); return; }
    verifyMut.mutate({ otpSessionId, code: otp, rememberMe }, {
      onSuccess: () => navigate('/admin', { replace: true }),
      onError:   (e) => setErr(e.message || 'Invalid code'),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-12 relative overflow-hidden">

      {/* Background brand accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-sky-500/5 blur-3xl rounded-full pointer-events-none"></div>

      <div className="w-full max-w-md relative">

        {/* Logo header */}
        <div className="flex flex-col items-center mb-8">
          <img src="/kejja-white.svg" alt="Kejja" className="h-10 w-auto mb-3" />
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30">
            <Shield className="w-3 h-3 text-rose-400" />
            <span className="text-[10px] font-semibold text-rose-300 uppercase tracking-widest">Admin Portal</span>
          </div>
        </div>

        <div className="bg-slate-900/80 backdrop-blur border border-slate-800 rounded-xl shadow-2xl p-8">
          {err && (
            <div className="flex items-start gap-2 bg-rose-950/40 border border-rose-900/60 text-rose-300 rounded-lg px-3 py-2.5 mb-5 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          )}

          {step === 'credentials' && (
            <form onSubmit={submitCredentials} className="space-y-4">
              <div>
                <h1 className="text-xl font-bold text-white">Sign in to admin</h1>
                <p className="text-xs text-slate-400 mt-1">Restricted to super administrators.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Email</label>
                <input
                  type="email" autoFocus required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@kejja.tech"
                  className="w-full h-10 px-3 rounded-md bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'} required autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full h-10 px-3 pr-10 rounded-md bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-white transition"
                    tabIndex={-1} aria-label={showPwd ? 'Hide password' : 'Show password'}>
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-950 text-rose-600 focus:ring-rose-600 focus:ring-offset-slate-900" />
                <span className="text-xs text-slate-300">Keep me signed in for 30 days</span>
              </label>

              <button
                type="submit"
                disabled={loginMut.isPending || !email || !password}
                className="w-full h-10 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md transition flex items-center justify-center gap-1.5"
              >
                {loginMut.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</>
                                    : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>

              <p className="text-[11px] text-slate-500 text-center pt-1">
                A 6-digit code will be emailed to you.
              </p>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={submitOtp} className="space-y-4">
              <div>
                <div className="h-10 w-10 rounded-lg bg-sky-500/15 text-sky-400 flex items-center justify-center mb-3">
                  <Mail className="w-5 h-5" />
                </div>
                <h1 className="text-xl font-bold text-white">Enter verification code</h1>
                <p className="text-xs text-slate-400 mt-1">
                  We sent a 6-digit code to <strong className="text-slate-300">{maskedEmail}</strong>. Expires in 10 minutes.
                </p>
              </div>

              <div>
                <input
                  ref={otpRef}
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full h-14 px-4 rounded-md bg-slate-950 border border-slate-700 text-white placeholder-slate-600 text-center font-mono text-2xl tracking-[0.4em] focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition"
                />
              </div>

              <button
                type="submit"
                disabled={verifyMut.isPending || otp.length !== 6}
                className="w-full h-10 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md transition flex items-center justify-center gap-1.5"
              >
                {verifyMut.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                                     : <><KeyRound className="w-4 h-4" /> Verify and sign in</>}
              </button>

              <button type="button"
                onClick={() => { setStep('credentials'); setOtp(''); setErr(null); }}
                className="w-full text-xs text-slate-400 hover:text-white">
                Use different credentials
              </button>
            </form>
          )}
        </div>

        <p className="text-[11px] text-slate-600 text-center mt-6">
          © {new Date().getFullYear()} Wekkitech Ltd. · mng.kejja.tech
        </p>
      </div>
    </div>
  );
}
